/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Input, Output, EventEmitter, Directive, HostListener, OnChanges, SecurityContext, SimpleChanges, ElementRef, OnInit } from "@angular/core";

import { DomSanitizer } from '@angular/platform-browser';
import { Debounce } from '../decorators/debounce.decorator';

/* TJB
 * Stolen from: https://stackoverflow.com/questions/35378087/how-to-use-ngmodel-on-divs-contenteditable-in-angular2/35383589#35383589
 * Modified to work with innerHtml
 */
@Directive({
  selector: '[appContentEditable]'
})
export class ContentEditableDirective implements OnChanges, OnInit {

  //The main input for the directive is the string that is being modeled
  // tslint:disable-next-line:no-input-rename
  @Input('appContentEditable')
  mModel: string;
  
  //Keywords allowed to be in the Model, needed for the conversion to HTML and back
  @Input()
  set keywords(keywords: Keyword[]) {
    this.mKeywords = keywords;
    this.refreshView();
  }

  mKeywords: Keyword[];

  //Event which gets emitted whenever the Model should be changed.  Can't seem to get two-way binding to work without this.
  @Output()
  modelChange = new EventEmitter<string>();
  
  //Holds the last displayed/translated Model.  Used for checks on whether to throw an update Event or refresh any changes (The update event itself will trigger ngOnChanges() which needs to then ignore it to preserver cursor position)
  mLastModel: string;
  
  
  constructor(
    private mElementRef: ElementRef,
    private mSanitizer: DomSanitizer
  ) { }
  
  
  ngOnInit(): void {
    this.refreshView();
  }

  /*
   * On Model Change, check to see if the View needs to be updated and if so update it
   */
  ngOnChanges(changes: SimpleChanges): void {
    console.log("ngOnChanges()");
    if (this.mModel != this.mLastModel) {
      this.refreshView();
    }
  }
  
  /*
   * Called by the component using this, used to insert a Keyword if the cursor is in 
   * the div
   */
  public addKeyword(keyword: Keyword): void {
    console.log("addKeyword()");

    let selection: Selection = null;
    let range: Range = null;
    if (window.getSelection() != null) {
      selection = window.getSelection();
      if (selection.rangeCount != null) {
        range = selection.getRangeAt(0);
        let container = range.commonAncestorContainer;
        let containerId: string = container['id'];
        while (container.nodeType != Node.ELEMENT_NODE || (containerId == null || containerId != this.mElementRef.nativeElement.id)) {
          container = container.parentNode;
          if (container == null) {
            break;
          } else {
            containerId = container['id'];
          }
        }
        
        if (containerId != null && containerId == this.mElementRef.nativeElement.id) {
          //Delete the contents of the Range (This erases anything that was previously highlighted)
          range.deleteContents();
          
          //Insert the html for the new Keyword
          const div = document.createElement('div');
          div.innerHTML = keyword.button;
          const button = div.firstChild;
          range.insertNode(button);
          
          //Update the cursor position
          range.setStartAfter(button);
          range.setEndAfter(button);
          selection.removeAllRanges();
          selection.addRange(range);
          
          //Send out a model update event if needed (should be)
          this.onInput(false);
        } else {
          console.log("Ignoring Keyword: {} since the Window Selection is not in the container.", keyword);
        }
      } else {
        console.log("Ignoring Keyword: {} since the Window Selection doesn't have a range.", keyword);
      }
    } else {
      console.log("Ignoring Keyword: {} since there is no Window Selection.", keyword);
    }
  }
  
  /*
   * Key Down intercepter
   * Used to disable allowing Users to put line breaks into their message
   * until that is figured out on the Server. (What should be used for line breaks?)
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): boolean {
    switch (event.keyCode) {
      case 13: {  //Enter key
        event.stopPropagation();
        return false;
      }
      default: {
        return true;
      }
    }
  }
  
  /*
   * This disables the ability of Users to drag+drop contents into the text area.
   */
  @HostListener('drop', ['$event'])
  onDrop(event: any): void {
    event.preventDefault();
    event.stopPropagation();
  }
  
  
  /*
   * Input intercepters
   * Whenever the User changes the Input, send out an event with the updated Model information
   */
  @HostListener('input', ['false'])
  @Debounce(1000)
  onInput(trim: boolean): void {

    const value = this.convertUiToBackend(this.mElementRef.nativeElement.innerHTML);
    if (value != this.mLastModel) {
      this.mLastModel = value;
      this.modelChange.emit(value);
    }
  }
  
  /*
   * Refreshes the View from the current Model.
   */
  private refreshView(): void {
    if(this.mModel != null){
      this.mLastModel = this.mModel;
      const newContent = this.convertBackendToUi(this.mSanitizer.sanitize(SecurityContext.HTML, this.mModel));
      if (newContent !== this.mElementRef.nativeElement.innerHTML) {
        this.mElementRef.nativeElement.innerHTML = newContent;
      }
    }
  }
  
  /*
   * Converts any found Keyword Tokens in the given string to their button representation
   */
  private convertBackendToUi(model: string): string {
    let rv = model;
    if (rv != null) {
      if (this.mKeywords != null) {
        for (const keyword of this.mKeywords) {
          rv = this.replace(rv, keyword.token, keyword.button);
        }
      }
    }
    console.log("convertBackendToUi()\n\tFrom Model: '" + model + "'\n\tTo UI: '" + rv + "'...");
    return rv;
  }
  
  /*
   * Converts the information in the UI to the corresponding Model
   * Swaps out any Keyword's HTML with their Token representation, and converts extra HTML that might get added.
   */
  private convertUiToBackend(uiValue: string): string {
    let rv = uiValue;
    if (rv != null) {
      //1) Replace any "&nbsp;" strings with a space (" ") and remove any "<br>" that get added. (Firefox is added these to the end for some reason...)
      rv = this.replace(this.replace(rv, "&nbsp;", " "), "<br>", "");
      
      //2) Cycle through the Keywords, replacing their UI representation with their Token Representation
      if (this.mKeywords != null) {
        for (const keyword of this.mKeywords) {
          rv = this.replace(rv, keyword.button, keyword.token);
        }
      }
      
      //rv = rv.replace(/^[\n\s]+/, '');
      //rv = rv.replace(/[\n\s]+$/, '');
    }
    console.log("convertUiToBackend()\n\tFrom UI: '" + uiValue + "'\n\tTo Model: '" + rv + "'");
    return rv;
  }
  
  /*
   * Replaces all occurances of "oldValue" with "newValue" in the "message" and returns the result
   */
  private replace(message: string, oldValue: string, newValue: string): string {
    let rv = message;
    if (rv != null && oldValue != null && newValue != null) {
      let index: number;
      while ((index = rv.indexOf(oldValue)) > -1) {
        const newRv = rv.substring(0, index) + newValue + rv.substring(index + oldValue.length, rv.length);
        rv = newRv;
      }
    }
    return rv;
  }

}

/*
 * Keyword construct
 * TODO: Button might be able to be removed from here.  If we dynamically create the string representation each one... Not sure if that's best atm.
 */
export class Keyword {
  name: string;
  classes: string[];
  token: string;
  button: string;
}
